import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import Upload from "@/pages/Upload";
import PracticeFormat from "@/pages/PracticeFormat";
import Interview from "@/pages/Interview";
import Prepare from "@/pages/Prepare";
import ReportAccessGate from "@/pages/ReportAccessGate";
import FreeComplete from "@/pages/FreeComplete";
import Downloads from "@/pages/Downloads";
import BetaFeedback from "@/pages/BetaFeedback";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import ProfileSetup from "@/pages/ProfileSetup";
import AccountSettings from "@/pages/AccountSettings";
import WaitingBeta from "@/pages/WaitingBeta";
import GoogleConsent from "@/pages/GoogleConsent";
import AdminHome from "@/pages/AdminHome";
import AdminBetaCodes from "@/pages/AdminBetaCodes";
import AdminBetaFeedback from "@/pages/AdminBetaFeedback";
import AdminUsers from "@/pages/AdminUsers";
import AdminAudit from "@/pages/AdminAudit";
import AdminPrepFailures from "@/pages/AdminPrepFailures";
import ProtectedRoute from "@/components/ProtectedRoute";
import SessionAllowanceTracker from "@/components/SessionAllowanceTracker";
import InterviewLifecycleControls from "@/components/InterviewLifecycleControls";
import QuestionTranslationAid from "@/components/QuestionTranslationAid";
import { AuthProvider, useAuth } from "@/context/AuthContext";

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return children;
}
import { Toaster } from "sonner";

/*
 * Google OAuth: the backend (/api/auth/google/callback) sets the session
 * cookie and then redirects the browser to `/auth/callback`, where
 * AuthCallback simply loads the session and hands over to ProtectedRoute.
 */

function AppRoutes() {
  const location = useLocation();

  // Best-effort immediate privacy cleanup for an unfinished interview when
  // the browser page is closed/reloaded/navigated away. A keepalive fetch is
  // attempted first because it explicitly carries same-origin credentials;
  // sendBeacon remains the fallback. Neither path delays window shutdown.
  //
  // IMPORTANT: this deletes only temporary interview working data. It does
  // NOT reverse the interview charge/count recorded when the interview began.
  // The server-side 60-minute TTL remains the guaranteed fallback when an exit
  // request cannot be delivered (power loss, process kill, network failure).
  useEffect(() => {
    const match = location.pathname.match(/^\/interview\/([^/]+)$/);
    if (!match) return undefined;
    const sessionId = match[1];
    const abandonUrl = `/api/sessions/${encodeURIComponent(sessionId)}/abandon`;
    let sent = false;

    const abandonOnPageExit = () => {
      if (
        window.__MEC_RESTARTING_SESSION__ === sessionId ||
        window.__MEC_ENDING_SESSION__ === sessionId
      ) {
        return;
      }
      if (sent) return;
      sent = true;

      try {
        fetch(abandonUrl, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          cache: "no-store",
        }).catch(() => {
          try {
            if (navigator.sendBeacon) navigator.sendBeacon(abandonUrl);
          } catch { /* TTL fallback */ }
        });
      } catch {
        try {
          if (navigator.sendBeacon) navigator.sendBeacon(abandonUrl);
        } catch { /* TTL fallback */ }
      }
    };

    window.addEventListener("beforeunload", abandonOnPageExit);
    window.addEventListener("pagehide", abandonOnPageExit);
    return () => {
      window.removeEventListener("beforeunload", abandonOnPageExit);
      window.removeEventListener("pagehide", abandonOnPageExit);
    };
  }, [location.pathname]);

  useEffect(() => {
    const match = location.pathname.match(/^\/report\/([^/]+)$/);
    if (!match) return undefined;
    const sessionId = match[1];
    const closeUrl = `/api/sessions/${encodeURIComponent(sessionId)}/close-repractice`;
    let sent = false;

    const closeRepracticeWindow = () => {
      if (sent) return;
      sent = true;
      try {
        fetch(closeUrl, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          cache: "no-store",
        }).catch(() => {
          try {
            if (navigator.sendBeacon) navigator.sendBeacon(closeUrl);
          } catch { /* server-side auth/session enforcement is the backstop */ }
        });
      } catch {
        try {
          if (navigator.sendBeacon) navigator.sendBeacon(closeUrl);
        } catch { /* server-side auth/session enforcement is the backstop */ }
      }
    };

    window.addEventListener("beforeunload", closeRepracticeWindow);
    window.addEventListener("pagehide", closeRepracticeWindow);
    return () => {
      window.removeEventListener("beforeunload", closeRepracticeWindow);
      window.removeEventListener("pagehide", closeRepracticeWindow);
      closeRepracticeWindow();
    };
  }, [location.pathname]);

  return (
    <>
      <SessionAllowanceTracker />
      <InterviewLifecycleControls />
      <QuestionTranslationAid />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/change-password" element={<ProtectedRoute allow={["/change-password", "/consent", "/setup", "/waiting-beta"]}><ChangePassword /></ProtectedRoute>} />
        <Route path="/consent" element={<ProtectedRoute allow={["/consent", "/setup", "/waiting-beta"]}><GoogleConsent /></ProtectedRoute>} />
        <Route path="/setup" element={<ProtectedRoute allow={["/setup", "/waiting-beta"]}><ProfileSetup /></ProtectedRoute>} />
        <Route path="/waiting-beta" element={<ProtectedRoute allow={["/waiting-beta"]}><WaitingBeta /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><PracticeFormat /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
        <Route path="/prepare/:sessionId" element={<ProtectedRoute><Prepare /></ProtectedRoute>} />
        <Route path="/interview/:sessionId" element={<ProtectedRoute><Interview /></ProtectedRoute>} />
        <Route path="/report/:sessionId" element={<ProtectedRoute><ReportAccessGate /></ProtectedRoute>} />
        <Route path="/free-complete" element={<ProtectedRoute><FreeComplete /></ProtectedRoute>} />
        <Route path="/downloads" element={<ProtectedRoute><Downloads /></ProtectedRoute>} />
        <Route path="/beta-feedback/:sessionId" element={<ProtectedRoute><BetaFeedback /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminHome /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/beta-codes" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminBetaCodes /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/beta-feedback" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminBetaFeedback /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminUsers /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/audit" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminAudit /></AdminOnly></ProtectedRoute>} />
        <Route path="/admin/prep-failures" element={<ProtectedRoute allow={["/waiting-beta"]}><AdminOnly><AdminPrepFailures /></AdminOnly></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}
